/**
 * Formatadores utilitários (pt-BR): CNPJ, CEP, moeda, data, CNAE, telefone.
 *
 * As datas do contrato chegam como `YYYY-MM-DD`; a formatação é feita por
 * fatiamento de string (sem `new Date`) para não sofrer deslocamento de fuso.
 */

const PLACEHOLDER = "—";

const currencyFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

const numberFormatter = new Intl.NumberFormat("pt-BR");

/** Remove tudo que não for dígito. */
export function onlyDigits(value: string | null | undefined): string {
  return (value ?? "").replace(/\D/g, "");
}

/** Formata um CNPJ de 14 dígitos como `XX.XXX.XXX/XXXX-XX`. */
export function formatCnpj(cnpj: string | null | undefined): string {
  const digits = onlyDigits(cnpj);
  if (digits.length !== 14) {
    return cnpj ?? "";
  }
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(
    8,
    12,
  )}-${digits.slice(12, 14)}`;
}

/**
 * Formata progressivamente os dígitos de um CNPJ como `XX.XXX.XXX/XXXX-XX`,
 * aplicando a máscara conforme o usuário digita (aceita 0 a 14 dígitos).
 */
export function formatCnpjPartial(value: string | null | undefined): string {
  const digits = onlyDigits(value).slice(0, 14);
  let out = digits.slice(0, 2);
  if (digits.length > 2) {
    out += `.${digits.slice(2, 5)}`;
  }
  if (digits.length > 5) {
    out += `.${digits.slice(5, 8)}`;
  }
  if (digits.length > 8) {
    out += `/${digits.slice(8, 12)}`;
  }
  if (digits.length > 12) {
    out += `-${digits.slice(12, 14)}`;
  }
  return out;
}

/** Formata a raiz do CNPJ (8 dígitos) como `XX.XXX.XXX`. */
export function formatCnpjBasico(cnpjBasico: string | null | undefined): string {
  const digits = onlyDigits(cnpjBasico);
  if (digits.length !== 8) {
    return cnpjBasico ?? "";
  }
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}`;
}

/** Formata um CEP de 8 dígitos como `XXXXX-XXX`. */
export function formatCep(cep: string | null | undefined): string {
  const digits = onlyDigits(cep);
  if (digits.length !== 8) {
    return cep ? cep : PLACEHOLDER;
  }
  return `${digits.slice(0, 5)}-${digits.slice(5, 8)}`;
}

/** Formata o código CNAE com 7 dígitos (`padStart`), conforme o contrato. */
export function formatCnae(codigo: number | null | undefined): string {
  if (codigo === null || codigo === undefined) {
    return PLACEHOLDER;
  }
  return String(codigo).padStart(7, "0");
}

/** Formata um valor monetário em Reais (pt-BR). Nulo vira travessão. */
export function formatCurrencyBRL(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return PLACEHOLDER;
  }
  return currencyFormatter.format(value);
}

/** Formata um inteiro/decimal em pt-BR (separador de milhar). */
export function formatNumber(value: number): string {
  return numberFormatter.format(value);
}

/** Formata uma data ISO `YYYY-MM-DD` como `dd/mm/aaaa`. Nulo vira travessão. */
export function formatDateBR(value: string | null | undefined): string {
  if (!value) {
    return PLACEHOLDER;
  }
  const [year, month, day] = value.slice(0, 10).split("-");
  if (!year || !month || !day) {
    return value;
  }
  return `${day}/${month}/${year}`;
}

/**
 * Formata um telefone a partir do DDD e do número. Devolve `null` quando não há
 * número — assim o chamador decide se renderiza o campo.
 */
export function formatPhone(
  ddd: string | null | undefined,
  numero: string | null | undefined,
): string | null {
  const area = onlyDigits(ddd);
  const digits = onlyDigits(numero);
  if (!digits) {
    return null;
  }
  let local = digits;
  if (digits.length === 9) {
    local = `${digits.slice(0, 5)}-${digits.slice(5)}`;
  } else if (digits.length === 8) {
    local = `${digits.slice(0, 4)}-${digits.slice(4)}`;
  }
  return area ? `(${area}) ${local}` : local;
}

/** Devolve os dígitos de um telefone (DDD + número) para uso em `tel:`. */
export function phoneHref(
  ddd: string | null | undefined,
  numero: string | null | undefined,
): string {
  return `+55${onlyDigits(ddd)}${onlyDigits(numero)}`;
}

/** Trunca um texto adicionando reticências quando excede o limite. */
export function truncate(value: string, max: number): string {
  if (value.length <= max) {
    return value;
  }
  return `${value.slice(0, max - 1).trimEnd()}…`;
}
